/**
 * Divetta Milano — single storefront contract for Flyweight entry points.
 *
 * Flyweight remains the only chat provider and `flyweightchatopen` remains its
 * only open command. The bridge owns readiness, duplicate-open protection,
 * accessible state, focus return and the native assistant-page fallback.
 */
(() => {
  'use strict';

  const OPEN_EVENT = 'flyweightchatopen';
  const CONTEXT_EVENT = 'ld:assistant:context';
  const STATE_EVENT = 'ld:assistant:state';
  const CLOSE_REQUEST_EVENT = 'ld:assistant:request-close';
  const TRIGGER_SELECTOR = '[data-ld-ai-trigger], [data-r7-open-ai], [data-r7-open-product-ai]';
  const storefrontRoot = window.Shopify?.routes?.root || '/';
  const DEFAULT_FALLBACK = document.body?.dataset.ldAiFallback || storefrontRoot;
  const PROVIDER_SCRIPT_FRAGMENT = 'assistant.flyweight.io/chat/index.js';
  const PROVIDER_SELECTOR = [
    'iframe[src*="assistant.flyweight.io"]',
    '#flyweight-chat > iframe[title="Chat"]',
    '#flyweight-chat > iframe[title="Chat popup"]',
    '[data-flyweight-chat]',
    '[id*="flyweight" i]',
    '[class*="flyweight" i]'
  ].join(',');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let opening = false;
  let activeTrigger = null;
  let openingTimer = 0;
  let readinessTimer = 0;
  let closeWatchTimer = 0;
  let chatWasVisible = false;
  let statusRegion = null;
  let cachedSurface = null;
  let requestGeneration = 0, fallbackTimer = 0, closeTimer = 0;
  let lastCloseRequestAt = -Infinity;
  let currentContextKey = '';
  const cancelPending = () => {
    requestGeneration += 1;
    window.clearTimeout(openingTimer);
    window.clearTimeout(readinessTimer);
    window.clearTimeout(fallbackTimer);
    window.clearTimeout(closeTimer);
    openingTimer = readinessTimer = fallbackTimer = closeTimer = 0;
    opening = false;
  };
  const providerScopes = [document];
  const providerScopeSet = new WeakSet([document]);

  // Article discovery keeps Shopify's native form/results. Flyweight chat stays
  // available; only its separate search launcher is released on these routes.
  let nativeSearchTimer = 0;
  const usesNativeEditorialSearch = () => {
    const url = new URL(window.location.href);
    const base = new URL(storefrontRoot, url.origin).pathname.replace(/\/?$/, '/');
    const path = url.pathname.replace(/\/+$/, '');
    if (path === `${base}search`) {
      const types = url.searchParams.getAll('type');
      return types.length === 1 && types[0] === 'article';
    }
    const journal = `${base}blogs/notizie`;
    return (path === journal || path.startsWith(`${journal}/tagged/`)) && Boolean(
      document.querySelector('.ldj-discovery form.ldj-search input[name="type"][value="article"]')
    );
  };
  const releaseEditorialSearch = () => {
    nativeSearchTimer = 0;
    if (!usesNativeEditorialSearch()) return;
    document.querySelectorAll('[data-flyweight-chat][data-search="true"]:not(script)').forEach((node) => {
      if (!node.id) return;
      const instance = window.FlyweightChat?.instances?.[node.id];
      if (typeof instance?.destroy !== 'function') return;
      instance.destroy();
      // An unrelated later bundle must not automatically remount this anchor.
      node.removeAttribute('data-flyweight-chat');
    });
  };
  const scheduleEditorialSearchRelease = () => {
    if (nativeSearchTimer || !usesNativeEditorialSearch()) return;
    // Ready is emitted inside React effects; release after their commit finishes.
    nativeSearchTimer = window.setTimeout(releaseEditorialSearch, 0);
  };
  const onProviderReady = (event) => {
    if (event.detail?.kind === 'floating-search' && event.detail.ready === true) {
      scheduleEditorialSearchRelease();
    }
  };

  const registerShadowRoots = (scope) => {
    const candidates = [];
    if (scope instanceof Element) candidates.push(scope);
    scope?.querySelectorAll?.('*').forEach((element) => candidates.push(element));
    candidates.forEach((element) => {
      if (!element.shadowRoot || providerScopeSet.has(element.shadowRoot)) return;
      providerScopeSet.add(element.shadowRoot);
      providerScopes.push(element.shadowRoot);
      registerShadowRoots(element.shadowRoot);
    });
  };

  const parentAcrossShadow = (node) => {
    if (node?.parentElement) return node.parentElement;
    return node?.getRootNode?.()?.host || null;
  };

  const belongsToEmbeddedConcierge = (node) => {
    let current = node;
    for (let depth = 0; current && depth < 12; depth += 1) {
      if (current instanceof Element && current.matches('[data-ld-flyweight-embedded]')) return true;
      current = parentAcrossShadow(current);
    }
    return false;
  };

  const providerNodes = () => {
    const nodes = [];
    providerScopes.forEach((scope) => {
      scope.querySelectorAll?.(PROVIDER_SELECTOR).forEach((node) => {
        if (
          node instanceof HTMLElement &&
          !['SCRIPT', 'LINK', 'STYLE'].includes(node.tagName) &&
          !node.matches('.ld-flyweight-brand-launcher, [data-ld-ai-trigger], [data-r7-open-ai], [data-r7-open-product-ai]') &&
          !belongsToEmbeddedConcierge(node)
        ) nodes.push(node);
      });
    });
    return nodes;
  };

  const providerScripts = () => Array.from(document.scripts).filter((script) =>
    script.src.includes(PROVIDER_SCRIPT_FRAGMENT)
  );

  const elementIsVisible = (element) => {
    if (!(element instanceof HTMLElement) || !element.isConnected || element.hidden) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number.parseFloat(style.opacity || '1') > 0.02 &&
      rect.width > 1 &&
      rect.height > 1;
  };

  const elementIsExpandedChatSurface = (element) => {
    if (!elementIsVisible(element)) return false;
    if (element instanceof HTMLIFrameElement) {
      const providerHeight = element.style.height;
      const providerWidth = element.style.width;
      if ((providerHeight.endsWith('px') && Number.parseFloat(providerHeight) < 280) ||
          (providerWidth.endsWith('px') && Number.parseFloat(providerWidth) < 280)) return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.width >= 280 && rect.height >= 280;
  };

  const visibleChatSurface = () => {
    if (elementIsExpandedChatSurface(cachedSurface)) return cachedSurface;
    cachedSurface = null;
    const candidates = providerNodes();

    for (const node of candidates) {
      let current = node;
      for (let depth = 0; current && depth < 10; depth += 1) {
        if (current === document.body || current === document.documentElement) break;
        if (current instanceof HTMLElement && elementIsExpandedChatSurface(current)) {
          cachedSurface = current;
          return current;
        }
        current = parentAcrossShadow(current);
      }
    }

    return null;
  };

  const providerState = () => {
    const providerFrameReady = providerNodes().some((node) =>
      node instanceof HTMLIFrameElement && node.src.includes('assistant.flyweight.io')
    );
    if (typeof window.FlyweightChat !== 'undefined' || providerFrameReady) return 'ready';

    const scripts = providerScripts();
    if (scripts.some((script) => script.dataset.ldFlyweightLoadState === 'ready')) return 'ready';
    if (scripts.some((script) => window.performance?.getEntriesByName?.(script.src)
      .some((entry) => entry.responseEnd > 0))) return 'ready';
    if (scripts.some((script) => script.dataset.ldFlyweightLoadState !== 'error')) return 'loading';
    return 'missing';
  };

  const fallbackUrl = (trigger) => {
    const requested = trigger.dataset.ldAiFallback ||
      (trigger instanceof HTMLAnchorElement ? trigger.getAttribute('href') : '');
    for (const value of [requested, DEFAULT_FALLBACK, storefrontRoot]) {
      if (!value) continue;
      try {
        const url = new URL(value, window.location.href);
        if (!['http:', 'https:'].includes(url.protocol) || url.origin !== window.location.origin) continue;
        if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) {
          const id = decodeURIComponent(url.hash.slice(1));
          if (!document.getElementById(id) && !document.getElementsByName(id).length) continue;
        }
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_error) {
        // Try the configured fallback, then the locale-aware storefront root.
      }
    }
    return '/';
  };

  const ensureStatusRegion = () => {
    if (statusRegion?.isConnected) return statusRegion;
    statusRegion = document.createElement('div');
    statusRegion.className = 'visually-hidden';
    statusRegion.setAttribute('role', 'status');
    statusRegion.setAttribute('aria-live', 'polite');
    statusRegion.setAttribute('aria-atomic', 'true');
    statusRegion.dataset.ldAiStatus = '';
    (document.body || document.documentElement).appendChild(statusRegion);
    return statusRegion;
  };

  const announce = (message) => {
    const region = ensureStatusRegion();
    region.textContent = '';
    window.setTimeout(() => {
      if (region.isConnected) region.textContent = message;
    }, reducedMotion.matches ? 0 : 30);
  };


  const emitState = (state, trigger = activeTrigger, extra = {}) => {
    const assistantVisible = state === 'waiting' || state === 'opening' || state === 'opened' || state === 'closing';
    const source = String(trigger?.dataset?.ldAiSource || '');
    const homeCentered = assistantVisible && source.startsWith('home');
    document.body?.classList.toggle('lda-assistant-open', assistantVisible);
    document.body?.classList.toggle('lda-assistant-home-center', homeCentered);
    document.dispatchEvent(new CustomEvent(STATE_EVENT, {
      detail: Object.freeze({
        state,
        trigger: trigger instanceof HTMLElement ? trigger : null,
        ...extra
      })
    }));
  };

  const setTriggerState = (trigger, state, expanded = false) => {
    if (!(trigger instanceof HTMLElement)) return;
    trigger.dataset.ldAiState = state;
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (state === 'opening' || state === 'waiting') trigger.setAttribute('aria-busy', 'true');
    else trigger.removeAttribute('aria-busy');
  };

  const initializeTriggers = (scope = document) => {
    scope.querySelectorAll?.(TRIGGER_SELECTOR).forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      if (!trigger.hasAttribute('aria-haspopup')) trigger.setAttribute('aria-haspopup', 'dialog');
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
      if (!trigger.dataset.ldAiState) trigger.dataset.ldAiState = 'idle';
    });
  };

  const safeContext = (trigger) => {
    const main = document.querySelector('main[data-template]');
    const source = String(trigger.dataset.ldAiSource || 'storefront')
      .replace(/[^a-z0-9_-]/gi, '')
      .slice(0, 48) || 'storefront';
    const template = String(main?.dataset.template || document.body?.dataset.template || '')
      .replace(/[^a-z0-9_.-]/gi, '')
      .slice(0, 64);
    const mode = String(trigger.dataset.ldAiMode || (trigger.hasAttribute('data-r7-open-product-ai') ? 'product' : '') || document.documentElement.dataset.ldPageType || 'storefront')
      .replace(/[^a-z0-9_-]/gi, '')
      .slice(0, 32) || 'storefront';
    const entity = String(trigger.dataset.ldAiContext || '')
      .replace(/[^a-z0-9:_-]/gi, '')
      .slice(0, 96);
    const collectionTitle = String(trigger.dataset.ldAiCollectionTitle || '')
      .replace(/[<>]/g, '')
      .trim()
      .slice(0, 96);
    const cleanId = (value) => String(value || '').replace(/[^0-9]/g, '').slice(0, 32);
    const owner = trigger.closest('[data-ld-pdp-engine], [data-ldx-product-scene]') ||
      trigger.parentElement?.closest('[data-product-id]');
    const explicitId = cleanId(trigger.dataset.productId);
    const ownerId = cleanId(owner?.dataset.productId);
    const requestedId = explicitId || ownerId;
    const ownerMatches = !explicitId || explicitId === ownerId;
    let snapshot = null;
    // A recommendation must never inherit the main PDP's active variant.
    if (requestedId) {
      const candidates = owner
        ? Array.from(owner.querySelectorAll('[data-ld-product-snapshot]')) : [];
      document.querySelectorAll('[data-ld-product-snapshot]').forEach((node) => {
        if (!candidates.includes(node)) candidates.push(node);
      });
      for (const node of candidates) {
        try {
          const candidate = JSON.parse(node.textContent || '');
          if (cleanId(candidate?.product?.id) === requestedId) {
            snapshot = candidate;
            break;
          }
        } catch (_error) { /* An invalid snapshot is not product evidence. */ }
      }
    }
    const productId = requestedId;
    const productHandle = String(trigger.dataset.productHandle || snapshot?.product?.handle ||
      (ownerMatches ? owner?.dataset.productHandle : '') || '')
      .replace(/[^a-z0-9_-]/gi, '').slice(0, 128);
    const productTitle = String(trigger.dataset.productTitle || snapshot?.product?.title ||
      (ownerMatches ? owner?.dataset.productTitle : '') || '')
      .replace(/[<>]/g, '').trim().slice(0, 160);
    let activeVariant = snapshot?.variant || null;
    if (owner && ownerMatches) {
      const select = owner.querySelector('[data-ld-collection-variant], [data-ld-story-variant]');
      const hidden = owner.querySelector('[data-ld-collection-variant-id], [data-ld-story-variant-id]');
      if (select) {
        activeVariant = select.value ? { id: select.value, title: select.selectedOptions?.[0]?.textContent || '' } : null;
      } else if (hidden) {
        activeVariant = hidden.value ? { id: hidden.value, title: '' } : null;
      }
    }
    const variantId = String(activeVariant?.id || '')
      .replace(/[^0-9]/g, '')
      .slice(0, 32);
    const variantTitle = String(activeVariant?.title || '')
      .replace(/[<>]/g, '')
      .trim()
      .slice(0, 160);
    let productUrl = '';
    const requestedUrl = trigger.dataset.productUrl || snapshot?.product?.url ||
      (ownerMatches ? owner?.dataset.productUrl : '') ||
      (productHandle ? `${storefrontRoot}products/${productHandle}` : '');
    if (requestedUrl) {
      try {
        const url = new URL(requestedUrl, window.location.origin);
        if (url.origin === window.location.origin && /\/products\/[^/]+\/?$/.test(url.pathname)) {
          url.search = '';
          url.hash = '';
          if (variantId) url.searchParams.set('variant', variantId);
          productUrl = url.href;
        }
      } catch (_error) { /* A context must point to a real storefront product route. */ }
    }

    return Object.freeze({
      version: 1,
      source,
      mode,
      pageType: String(document.documentElement.dataset.ldPageType || '').slice(0, 32),
      template,
      path: window.location.pathname,
      ...(entity ? { entity } : {}),
      ...(collectionTitle ? { collectionTitle } : {}),
      ...(productId ? { productId } : {}),
      ...(productHandle ? { productHandle } : {}),
      ...(productTitle ? { productTitle } : {}),
      ...(productUrl ? { productUrl } : {}),
      ...(variantId ? { variantId } : {}),
      ...(variantTitle ? { variantTitle } : {})
    });
  };

  const emitContext = (trigger) => {
    const context = safeContext(trigger);
    document.dispatchEvent(new CustomEvent(CONTEXT_EVENT, {
      detail: context
    }));
    return context;
  };

  const messageForContext = (context) => {
    if (!context?.productId || !context.productTitle || !context.productUrl) return '';
    const variant = context.variantTitle && context.variantTitle !== 'Default Title'
      ? ` Variante selezionata: ${context.variantTitle}.` : '';
    return `Vorrei informazioni su ${context.productTitle}.${variant} Il prodotto a cui mi riferisco ora è ${context.productUrl}. Puoi spiegarmi uso e caratteristiche di questo prodotto?`;
  };
  const contextKey = (context) => context?.productId ? `${context.productId}:${context.variantId || ''}` : '';

  const focusChatSurface = () => {
    const surface = visibleChatSurface();
    if (!surface) return;
    const focusTarget = surface.matches('iframe, button, [tabindex]')
      ? surface
      : surface.querySelector('iframe, button, [tabindex]');
    focusTarget?.focus?.({ preventScroll: true });
  };

  const stopCloseWatch = () => {
    window.clearInterval(closeWatchTimer);
    closeWatchTimer = 0;
  };

  const watchForClose = () => {
    stopCloseWatch();
    chatWasVisible = true;
    closeWatchTimer = window.setInterval(() => {
      if (elementIsExpandedChatSurface(cachedSurface)) return;
      stopCloseWatch();
      chatWasVisible = false;
      cachedSurface = null;
      if (!(activeTrigger instanceof HTMLElement)) return;
      const trigger = activeTrigger;
      activeTrigger = null;
      setTriggerState(trigger, 'closed', false);
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
      announce('Consulenza chiusa. Il focus è tornato al comando di apertura.');
      emitState('closed', trigger);
    }, 420);
  };

  const navigateToFallback = (trigger, message) => {
    cancelPending();
    const generation = requestGeneration;
    setTriggerState(trigger, 'fallback', false);
    announce(message);
    emitState('fallback', trigger, { message });
    fallbackTimer = window.setTimeout(() => {
      if (generation === requestGeneration && trigger.isConnected) window.location.assign(fallbackUrl(trigger));
    }, reducedMotion.matches ? 0 : 120);
  };

  const waitForVisibleChat = (trigger) => {
    const generation = requestGeneration;
    const startedAt = performance.now();
    const attempt = () => {
      if (generation !== requestGeneration || !trigger.isConnected) return;
      const surface = visibleChatSurface();
      if (surface) {
        opening = false;
        if (activeTrigger && activeTrigger !== trigger) setTriggerState(activeTrigger, 'closed', false);
        activeTrigger = trigger;
        setTriggerState(trigger, 'open', true);
        announce('Consulenza Divetta aperta.');
        focusChatSurface();
        watchForClose();
        emitState('opened', trigger, { surface });
        return;
      }

      if (performance.now() - startedAt >= 6000) {
        navigateToFallback(trigger, 'La chat non ha risposto. Apro la pagina Assistente.');
        return;
      }

      openingTimer = window.setTimeout(attempt, 140);
    };
    attempt();
  };

  const dispatchOfficialOpen = (trigger, collectionPrepared = false) => {
    const remainingCloseTime = 300 - (performance.now() - lastCloseRequestAt);
    if (remainingCloseTime > 0) {
      const generation = requestGeneration;
      openingTimer = window.setTimeout(() => {
        if (generation === requestGeneration && trigger.isConnected) dispatchOfficialOpen(trigger, collectionPrepared);
      }, remainingCloseTime);
      return;
    }
    const collection = trigger?.closest?.('[data-ldx-capelli-products]') ||
      document.querySelector('[data-ldx-capelli-products]');
    if (!collectionPrepared && typeof collection?.prepareFloatingAI === 'function') {
      const generation = requestGeneration;
      const nextKey = contextKey(safeContext(trigger));
      collection.prepareFloatingAI({ reset: Boolean(currentContextKey && currentContextKey !== nextKey) })
        .then(() => {
          if (generation === requestGeneration && opening) dispatchOfficialOpen(trigger, true);
        })
        .catch(() => {
          if (generation === requestGeneration && opening) {
            navigateToFallback(trigger, 'La chat non è disponibile. Apro la pagina Assistente.');
          }
        });
      return;
    }
    const context = emitContext(trigger);
    const message = messageForContext(context);
    currentContextKey = contextKey(context);
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: message ? { message } : undefined }));
    waitForVisibleChat(trigger);
  };

  const waitForRuntime = (trigger) => {
    const generation = requestGeneration;
    const startedAt = performance.now();
    const attempt = () => {
      if (generation !== requestGeneration || !trigger.isConnected) return;
      if (providerState() === 'ready') {
        setTriggerState(trigger, 'opening', false);
        dispatchOfficialOpen(trigger);
        return;
      }

      if (performance.now() - startedAt >= 6000) {
        navigateToFallback(trigger, 'La chat non è disponibile. Apro la pagina Assistente.');
        return;
      }

      readinessTimer = window.setTimeout(attempt, 140);
    };
    attempt();
  };

  const open = (trigger) => {
    if (!(trigger instanceof HTMLElement)) return false;

    const requestedContext = safeContext(trigger);
    const requestedKey = contextKey(requestedContext);
    const pendingKey = activeTrigger ? contextKey(safeContext(activeTrigger)) : '';
    const restartContext = () => {
      if (!close(false)) {
        lastCloseRequestAt = performance.now();
        window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: false }));
      }
      const generation = requestGeneration;
      const reopen = () => {
        if (generation !== requestGeneration || !trigger.isConnected) return;
        if (visibleChatSurface()) {
          if (performance.now() - lastCloseRequestAt >= 6000) {
            navigateToFallback(trigger, 'La consulenza non ha risposto. Apro la pagina Assistente.');
            return;
          }
          fallbackTimer = window.setTimeout(reopen, 120);
          return;
        }
        open(trigger);
      };
      fallbackTimer = window.setTimeout(reopen, 300);
      return true;
    };

    const visibleSurface = visibleChatSurface();
    if (visibleSurface) {
      if (requestedKey && requestedKey !== currentContextKey && messageForContext(requestedContext)) return restartContext();
      cancelPending();
      emitContext(trigger);
      if (activeTrigger && activeTrigger !== trigger) setTriggerState(activeTrigger, 'closed', false);
      activeTrigger = trigger;
      setTriggerState(trigger, 'open', true);
      focusChatSurface();
      watchForClose();
      return true;
    }

    if (opening) {
      if (requestedKey && requestedKey !== pendingKey && messageForContext(requestedContext)) return restartContext();
      setTriggerState(trigger, trigger === activeTrigger ? 'waiting' : 'idle', false);
      announce('La consulenza si sta già aprendo.');
      return true;
    }

    const state = providerState();
    if (state === 'missing') {
      setTriggerState(trigger, 'fallback', false);
      return false;
    }

    cancelPending();
    opening = true;
    activeTrigger = trigger;
    setTriggerState(trigger, state === 'ready' ? 'opening' : 'waiting', false);
    announce(state === 'ready' ? 'Apertura della consulenza Divetta…' : 'Preparazione della consulenza Divetta…');
    emitState(state === 'ready' ? 'opening' : 'waiting', trigger);

    if (state === 'ready') dispatchOfficialOpen(trigger);
    else waitForRuntime(trigger);
    return true;
  };

  const close = (restoreFocus = true) => {
    cancelPending();
    stopCloseWatch();
    const generation = requestGeneration;
    const surface = visibleChatSurface();
    const trigger = activeTrigger;
    if (!surface) {
      if (trigger instanceof HTMLElement) {
        setTriggerState(trigger, 'closed', false);
        if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
      }
      activeTrigger = null;
      opening = false;
      emitState('closed', trigger);
      return false;
    }

    emitState('closing', trigger, { surface });
    lastCloseRequestAt = performance.now();
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: false }));
    const startedAt = performance.now();
    const waitUntilClosed = () => {
      if (generation !== requestGeneration) return;
      const stillVisible = elementIsExpandedChatSurface(surface);
      if (stillVisible && performance.now() - startedAt >= 4000) {
        setTriggerState(trigger, 'open', true);
        watchForClose();
        emitState('opened', trigger, { surface, warning: 'close-not-confirmed' });
        return;
      }
      if (!stillVisible) {
        stopCloseWatch();
        cachedSurface = null;
        chatWasVisible = false;
        opening = false;
        activeTrigger = null;
        if (trigger instanceof HTMLElement) {
          setTriggerState(trigger, 'closed', false);
          if (restoreFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
        }
        announce('Consulenza chiusa.');
        emitState('closed', trigger);
        return;
      }
      closeTimer = window.setTimeout(waitUntilClosed, 120);
    };
    waitUntilClosed();
    return true;
  };

  const observeProviderScripts = () => {
    providerScripts().forEach((script) => {
      if (script.dataset.ldFlyweightBridgeObserved === 'true') return;
      script.dataset.ldFlyweightBridgeObserved = 'true';
      script.addEventListener('load', () => {
        script.dataset.ldFlyweightLoadState = 'ready';
      }, { once: true });
      script.addEventListener('error', () => {
        script.dataset.ldFlyweightLoadState = 'error';
      }, { once: true });
    });
  };

  const onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const trigger = event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;
    if (!(trigger instanceof HTMLElement)) return;

    if (open(trigger)) {
      event.preventDefault();
    } else {
      event.preventDefault();
      navigateToFallback(trigger, 'La chat non è disponibile. Apro la pagina Assistente.');
    }
  };

  const init = () => {
    registerShadowRoots(document);
    initializeTriggers();
    observeProviderScripts();
    ensureStatusRegion();
    window.addEventListener('flyweightready', onProviderReady);
    // Existing instances may have emitted ready before this bridge loaded.
    scheduleEditorialSearchRelease();

    const observer = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach((node) => registerShadowRoots(node)));
      observeProviderScripts();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('click', onClick);
    document.addEventListener(CLOSE_REQUEST_EVENT, (event) => {
      close(event.detail?.restoreFocus !== false);
    });
    document.addEventListener('shopify:section:load', (event) => {
      initializeTriggers(event.target);
      scheduleEditorialSearchRelease();
    });
    window.addEventListener('pagehide', () => {
      window.clearTimeout(nativeSearchTimer);
      nativeSearchTimer = 0;
      cancelPending();
      stopCloseWatch();
      document.querySelectorAll(TRIGGER_SELECTOR).forEach((trigger) => setTriggerState(trigger, 'closed', false));
      document.body?.classList.remove('lda-assistant-open', 'lda-assistant-home-center');
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      scheduleEditorialSearchRelease();
      const surface = visibleChatSurface();
      if (surface && activeTrigger?.isConnected) {
        setTriggerState(activeTrigger, 'open', true);
        emitState('opened', activeTrigger, { surface });
        watchForClose();
      } else {
        const trigger = activeTrigger;
        activeTrigger = null;
        cachedSurface = null;
        chatWasVisible = false;
        emitState('closed', trigger);
      }
    });
  };

  window.LDDivettaAssistant = Object.freeze({
    contextFor(trigger) {
      return trigger instanceof HTMLElement ? safeContext(trigger) : null;
    },
    messageForContext,
    open(trigger) {
      return open(trigger instanceof HTMLElement ? trigger : null);
    },
    close(options = {}) {
      return close(options.restoreFocus !== false);
    },
    isOpen() {
      return Boolean(visibleChatSurface());
    },
    eventName: OPEN_EVENT,
    contextEventName: CONTEXT_EVENT,
    stateEventName: STATE_EVENT
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
